import apiService, { ServiceType } from './api';

export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const NTH_ORDINALS = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'] as const;
export type NthOrdinal = (typeof NTH_ORDINALS)[number];
export const NTH_DAYS = ['DAY', 'WEEKDAY', ...WEEKDAYS] as const;
export type NthDay = (typeof NTH_DAYS)[number];

export type SmartBetaSchedule =
  | { frequency: 'WEEKLY'; interval: number; weekdays: Weekday[] }
  | { frequency: 'MONTHLY'; interval: number; mode: 'DAYS'; monthDays: number[] }
  | { frequency: 'MONTHLY'; interval: number; mode: 'NTH'; nth: NthOrdinal; nthDay: NthDay };

export interface SmartBetaWeight {
  symbol: string;
  weight: number;
}

/** A white-list row as saved: a fixed percent, or null for auto (the weighting method's share). */
export interface SmartBetaEntry {
  symbol: string;
  weight: number | null;
}

/** How what is left after cash and the fixed weights is split over the auto tickers. */
export type WeightingMethod = 'EQUAL' | 'MARKET_CAP' | 'INDUSTRY_MARKET_CAP';

export const SCREEN_EXCHANGES = ['HSX', 'HNX', 'UPCOM'] as const;
export const SCREEN_INDEXES = ['VN30'] as const;
/** Screens on price, volume and foreign flows; the same list as Core's `RANGE_SCREENS`. */
export type RangeMetric =
  | 'AVG_VALUE_20D'
  | 'VOLUME_RATIO_5_20'
  | 'CHANGE_5D'
  | 'CHANGE_20D'
  | 'CHANGE_60D'
  | 'PRICE_VS_MA50'
  | 'FROM_HIGH_60D'
  | 'VOLATILITY_20D'
  | 'RSI_14'
  | 'FOREIGN_NET_VALUE_20D'
  | 'FOREIGN_NET_RATIO_20D';
export type ScreenMetric = 'EXCHANGE' | 'INDEX' | RangeMetric;

/** One screen: EXCHANGE / INDEX keep tickers in `values`; a range keeps them in [min, max], either end open. */
export interface ScreenCriterion {
  metric: ScreenMetric;
  values?: string[];
  min?: number | null;
  max?: number | null;
}

export interface SmartBetaSettings {
  schedule: SmartBetaSchedule;
  cashWeight: number;
  whitelist: SmartBetaEntry[];
  weightingMethod?: WeightingMethod;
  blacklist: string[];
  criteria: ScreenCriterion[];
  /** How many screened tickers join, the most liquid first. */
  screenLimit?: number;
  /** What the screens picked at the last save or run; auto tickers beside the white list. */
  screened?: string[];
}

/** What a rebalance trades to: the settings without the schedule. */
export type SmartBetaBasket = Pick<
  SmartBetaSettings,
  'cashWeight' | 'whitelist' | 'blacklist' | 'criteria' | 'weightingMethod' | 'screenLimit' | 'screened'
>;

export interface ScreenRow {
  symbol: string;
  exchange: string;
  /** Thousand VND. */
  price: number;
  /** Every range metric; null when the history is too short to tell. */
  metrics: Record<RangeMetric, number | null>;
}

export interface ScreenResult {
  asOf: string | null;
  matched: number;
  picked: ScreenRow[];
}

export interface ScreenJob<T = unknown> {
  id: string;
  kind: 'PREVIEW' | 'APPLY';
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
  result?: T;
  error?: string;
}

export interface ScreenPreset {
  id: string;
  name: string;
  criteria: ScreenCriterion[];
  screenLimit: number;
}

export interface SmartBetaWallet {
  instrumentConfigId: string;
  exchange: string;
  symbol: string;
  currentCapital: string;
  holdingsCost: string;
  heldQty: string;
}

export interface SmartBetaView {
  dataConfigId: string;
  portfolioId: string;
  name: string | null;
  settings: SmartBetaSettings;
  wallets: SmartBetaWallet[];
  totalCapital: string;
  /** Next date the schedule names (YYYY-MM-DD); a closed day moves to the next trading day. */
  nextRebalance: string | null;
  /** Raw VND a withdraw pays at once, without selling. */
  idleCash: string;
  /** Raw VND withdrawn but not yet raised: the next run sells for it, then it is paid. */
  pendingWithdrawal: string;
  /** Entered for the coming rebalance; that run trades to it and makes it current. */
  next: (SmartBetaBasket & { savedAt: string }) | null;
  /** An End is selling everything; it closes once that run stops. */
  ending: { requestedAt: string } | null;
  /** A queued screen is picking that basket's tickers; poll the view until it is false. */
  screening?: { current: boolean; next: boolean };
  /** Why a basket's last pick failed; it keeps the pick before. */
  screenError?: { current?: string; next?: string } | null;
  endedAt: string | null;
  updatedAt: string;
}

export interface ImportableHolding {
  symbol: string;
  quantity: number;
  /** Broker average cost in VND per share; null when the broker reports none. */
  averagePrice: number | null;
}

export type ExecutionStatus = 'STARTING' | 'PASSED' | 'STOPPING' | 'STOP' | 'ERROR' | 'ABANDONED';

/** The worker's own report; money is thousand VND, quantities are shares. */
export interface RebalanceProgress {
  day: string;
  phase: 'waiting' | 'sell' | 'buy' | 'done' | 'stopped';
  plan: {
    capital: string;
    sell: Record<string, number>;
    buy: Record<string, string>;
    target: Record<string, string>;
  } | null;
  remaining_sell: Record<string, number>;
  remaining_buy: Record<string, string>;
  matched: Record<string, number>;
  updated_at: string;
}

export interface SmartBetaStatus {
  execution: { id: string; status: ExecutionStatus } | null;
  /** The most recent run, running or not: whose log the page shows. */
  lastExecution?: { id: string; status: ExecutionStatus; executionDate: string } | null;
  progress: RebalanceProgress | null;
  rebalanceToday: boolean;
}

export interface SmartBetaWithdrawResult {
  idempotencyKey: string;
  paidNow: string;
  pending: string;
  /** Present when a run was asked for; `startError` says why it did not start. */
  started?: boolean;
  startError?: string;
  config: SmartBetaView | null;
}

export interface SmartBetaDepositResult {
  idempotencyKey: string;
  legs: Record<string, number>;
  config: SmartBetaView | null;
}

const core = () => apiService.getService(ServiceType.CORE);

export const smartBetaApi = {
  /** null when the portfolio has no smart beta yet. */
  async get(portfolioId: string): Promise<SmartBetaView | null> {
    const res = await core().get('/smart-beta', { params: { portfolioId } });
    return (res.data || null) as SmartBetaView | null;
  },

  async save(portfolioId: string, settings: SmartBetaSettings): Promise<SmartBetaView> {
    const res = await core().put('/smart-beta', { portfolioId, ...settings });
    return res.data as SmartBetaView;
  },

  /** Queue a look at what the basket's screens pick today; poll `screenJob` for it. */
  async screen(basket: SmartBetaBasket): Promise<ScreenJob<ScreenResult>> {
    const res = await core().post('/smart-beta/screen', basket);
    return res.data as ScreenJob<ScreenResult>;
  },

  async screenJob(id: string): Promise<ScreenJob<ScreenResult>> {
    const res = await core().get(`/smart-beta/screen/jobs/${id}`);
    return res.data as ScreenJob<ScreenResult>;
  },

  async presets(): Promise<ScreenPreset[]> {
    const res = await core().get('/smart-beta/screen-presets');
    return res.data as ScreenPreset[];
  },

  async savePreset(name: string, criteria: ScreenCriterion[], screenLimit: number): Promise<ScreenPreset> {
    const res = await core().post('/smart-beta/screen-presets', { name, criteria, screenLimit });
    return res.data as ScreenPreset;
  },

  async deletePreset(id: string): Promise<void> {
    await core().delete(`/smart-beta/screen-presets/${id}`);
  },

  async saveNext(dataConfigId: string, basket: SmartBetaBasket): Promise<SmartBetaView> {
    const res = await core().put(`/smart-beta/${dataConfigId}/next`, basket);
    return res.data as SmartBetaView;
  },

  async discardNext(dataConfigId: string): Promise<SmartBetaView> {
    const res = await core().delete(`/smart-beta/${dataConfigId}/next`);
    return res.data as SmartBetaView;
  },

  async status(dataConfigId: string): Promise<SmartBetaStatus> {
    const res = await core().get(`/smart-beta/${dataConfigId}/status`);
    return res.data as SmartBetaStatus;
  },

  async start(dataConfigId: string): Promise<{ started: boolean; alreadyRunning: boolean; executionId: string }> {
    const res = await core().post(`/smart-beta/${dataConfigId}/start`, {});
    return res.data;
  },

  async stop(dataConfigId: string): Promise<{ stopped: boolean; status: string }> {
    const res = await core().post(`/smart-beta/${dataConfigId}/stop`, {});
    return res.data;
  },

  async deposit(dataConfigId: string, amount: number, idempotencyKey: string): Promise<SmartBetaDepositResult> {
    const res = await core().post(`/smart-beta/${dataConfigId}/deposit`, {
      amount,
      idempotency_key: idempotencyKey,
    });
    return res.data as SmartBetaDepositResult;
  },

  async withdraw(
    dataConfigId: string,
    amount: number,
    idempotencyKey: string,
    sellNow: boolean,
  ): Promise<SmartBetaWithdrawResult> {
    const res = await core().post(`/smart-beta/${dataConfigId}/withdraw`, {
      amount,
      idempotency_key: idempotencyKey,
      sell_now: sellNow,
    });
    return res.data as SmartBetaWithdrawResult;
  },

  async cancelWithdraw(dataConfigId: string): Promise<SmartBetaView> {
    const res = await core().delete(`/smart-beta/${dataConfigId}/withdraw`);
    return res.data as SmartBetaView;
  },

  async end(
    dataConfigId: string,
    sell: boolean,
  ): Promise<{ ended: boolean; selling?: boolean; view: SmartBetaView | null }> {
    const res = await core().post(`/smart-beta/${dataConfigId}/end`, { sell, confirm: 'END' });
    return res.data;
  },

  async importable(dataConfigId: string): Promise<ImportableHolding[]> {
    const res = await core().get(`/smart-beta/${dataConfigId}/importable`);
    return res.data as ImportableHolding[];
  },

  async importHoldings(dataConfigId: string, items: { symbol: string; quantity: number }[]): Promise<SmartBetaView> {
    const res = await core().post(`/smart-beta/${dataConfigId}/import`, { items });
    return res.data as SmartBetaView;
  },
};
